// A DESTINATION'S FOLDERS, DOWNLOAD AND UPLOAD, IN THE MAIN ACC / PROCORE WINDOW (owner
// 2026-10-26: "i don't see rvt download path in main connection popup, i only see upload
// rvt path, same for nwc and nwd - i need download and upload for both platforms, procore
// and acc, just like shopdrawing upload, download").
//
// One grid per destination:            Download          Upload
//   Shop drawings                       folder            folder
//   Models - default (all types)        folder            folder
//   .rvt / .nwc / .nwd                  folder            folder
// A cell shows the folder in effect: the destination's own, else (for a type) its models
// default, else the company default - and says which. Saved through the folder slots
// (back/routes/folderPaths.js), which uploads, downloads and syncs already read.
import React, { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import addNotification from "helpers/notify";
import FolderPicker from "./FolderPicker";
import { getProjectFolderPaths, saveProjectFolderPaths } from "./folderPathsApi";
import { refreshIntegrationCards } from "./useIntegrations";

export const ROWS = [
  { key: "sd", label: "Shop drawings", down: ["shopDrawingsDownload"], up: ["shopDrawingsUpload"] },
  { key: "base", label: "Models - default (all types)", down: ["modelsDownload", "base"], up: ["modelsUpload", "base"] },
  { key: "rvt", label: "Revit (.rvt)", down: ["modelsDownload", "rvt"], up: ["modelsUpload", "rvt"] },
  { key: "nwc", label: "Navisworks (.nwc)", down: ["modelsDownload", "nwc"], up: ["modelsUpload", "nwc"] },
  { key: "nwd", label: "Navisworks (.nwd / .nwf)", down: ["modelsDownload", "nwd"], up: ["modelsUpload", "nwd"] },
];

const at = (paths, [slot, level]) => {
  const v = paths && paths[slot];
  return level ? (v && v[level]) || null : v || null;
};

/** The folder in effect and why: { own, used, why }. */
export function cellOf(dest, ref) {
  const own = dest ? at(dest.own, ref) : null;
  const eff = dest ? at(dest.effective, ref) : null;
  if (own) return { own, used: own, why: "" };
  if (!eff || !eff.target) return { own: null, used: null, why: "" };
  const why = eff.from === "company" ? "company default" : eff.level === "base" && ref[1] && ref[1] !== "base" ? "models default" : "";
  return { own: null, used: eff.target, why };
}

const startFrom = (t) => {
  if (!t || !t.accountId || !t.containerId) return null;
  const [accountName, containerName] = String(t.path || "").split(" / ");
  return { accountId: t.accountId, containerId: t.containerId, accountName, containerName };
};

export default function DestinationFolders({ projectId, provider, destination, connection, browseAs, canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(null); // { ref, label, start }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getProjectFolderPaths(projectId));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load, destination]);

  if (error) return <p className="text-danger">{error}</p>;
  if (!data) return <Spin size="small" />;

  // this destination - or, before it has a row, the account it will use
  const accounts = (data.accounts || []).filter((a) => a.provider === provider);
  const account = accounts.find((a) => (a.destinations || []).some((d) => d.destination === destination))
    || accounts.find((a) => (connection ? a.connection === connection : a.primary)) || null;
  const dest = account && ((account.destinations || []).find((d) => d.destination === destination)
    || (!destination ? { own: null, effective: account.effective } : null));
  const editable = canEdit && !!account && !!(data.canEdit && data.canEdit[provider]);

  const save = async (ref, target, label) => {
    const [slot, level] = ref;
    const paths = { [slot]: level ? { [level]: target } : target };
    setSaving(true);
    try {
      await saveProjectFolderPaths(projectId, provider, { ...(destination ? { destination } : { connectionId: account.connection }), paths });
      addNotification(target ? `${label}: ${target.path}` : `${label} cleared`, "success");
      setPicking(null);
      await load();
      refreshIntegrationCards();
      if (onChanged) onChanged();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  if (picking) {
    return (
      <div className="integration-section" data-testid="dest-folders-picking">
        <p className="mb-2"><b>{picking.label}: open the folder</b></p>
        <FolderPicker
          provider={provider}
          connection={browseAs}
          mode="folder"
          start={picking.start}
          busy={saving}
          onCancel={() => setPicking(null)}
          onPick={(t) => save(picking.ref, { accountId: t.accountId, containerId: t.containerId, folderId: t.folderId, path: t.path }, picking.label)}
        />
      </div>
    );
  }

  const cell = (row, dir) => {
    const ref = dir === "down" ? row.down : row.up;
    const c = cellOf(dest, ref);
    const label = `${row.label} - ${dir === "down" ? "download" : "upload"}`;
    return (
      <td className="dest-folders-cell" data-testid={`dest-folder-${row.key}-${dir}`}>
        {c.used ? (
          <span className={c.own ? "integration-path" : "integration-path text-muted"} title={c.used.path}>{c.used.path}</span>
        ) : (
          <span className="text-muted">Not set</span>
        )}
        {c.why ? <small className="text-muted d-block">({c.why})</small> : null}
        {editable ? (
          <span className="d-block">
            <button className="integration-link-btn" style={{ marginLeft: 0 }} disabled={saving}
              onClick={() => setPicking({ ref, label, start: startFrom(c.used) })}>
              {c.own ? "Change" : "Choose"}
            </button>
            {c.own ? (
              <button className="integration-link-btn danger" disabled={saving} onClick={() => save(ref, null, label)}>Clear</button>
            ) : null}
          </span>
        ) : null}
      </td>
    );
  };

  return (
    <div className="integration-section" data-testid="dest-folders">
      <div className="integration-section-title">Folders - download and upload</div>
      {!account ? (
        <p className="text-muted mb-1">Choose the account for this destination first.</p>
      ) : (
        <table className="dest-folders">
          <thead>
            <tr><th /><th>Download (into BidLight)</th><th>Upload (from BidLight)</th></tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {cell(row, "down")}
                {cell(row, "up")}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <small className="text-muted d-block">
        A type left empty uses the models default; a folder left empty uses the company default (Profile, Procore / ACC folders).
        Model Files sync and Export to 3D read the download folders; Send to, sync uploads and approved versions go to the upload folders.
      </small>
    </div>
  );
}
